import React, { useState, useRef, useEffect } from 'react';
import { Mold } from '../../types/mold';
import { Users } from 'lucide-react';

interface CustomerFilterProps {
  molds: Mold[];
  activeCustomer: string;
  onCustomerChange: (customer: string) => void;
}

const CustomerFilter: React.FC<CustomerFilterProps> = ({
  molds,
  activeCustomer,
  onCustomerChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const customers = ['ALL', ...new Set(molds.map((mold) => mold.customer.code))].sort();
  const activeCustomerDisplay = activeCustomer === 'ALL' ? 'ลูกค้าทั้งหมด' : activeCustomer;
  
  const customerCounts = molds.reduce((acc, mold) => {
    const code = mold.customer.code;
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCustomerSelect = (customer: string) => {
    onCustomerChange(customer);
    setIsOpen(false);
  };

  return (
    <div className="relative z-40 mr-1" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-full
          ${activeCustomer === 'ALL' ? 'bg-blue-100 hover:bg-blue-200' : 'bg-indigo-100 hover:bg-indigo-200'}
          border border-transparent
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          transition-all duration-200
        `}
      >
        <Users className={`w-4 h-4 ${activeCustomer === 'ALL' ? 'text-blue-600' : 'text-indigo-600'}`} />
        <span className={`text-sm font-medium ${activeCustomer === 'ALL' ? 'text-blue-700' : 'text-indigo-700'}`}>
          {activeCustomerDisplay}
        </span>
        <svg
          className={`w-4 h-4 ${activeCustomer === 'ALL' ? 'text-blue-600' : 'text-indigo-600'} transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute mt-2 w-56 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 py-1">
          {customers.map((customer) => (
            <button
              key={customer}
              onClick={() => handleCustomerSelect(customer)}
              className={`
                w-full text-left px-4 py-2
                ${customer === activeCustomer ? 
                  (customer === 'ALL' ? 'bg-blue-50' : 'bg-indigo-50') : 
                  'hover:bg-gray-50'
                }
                flex items-center justify-between group
              `}
            >
              <div className="flex items-center gap-2">
                {activeCustomer === customer && (
                  <svg
                    className={`w-4 h-4 ${customer === 'ALL' ? 'text-blue-600' : 'text-indigo-600'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
                <span
                  className={`text-sm ${
                    activeCustomer === customer
                      ? customer === 'ALL' ? 'text-blue-600 font-medium' : 'text-indigo-600 font-medium'
                      : 'text-gray-700 group-hover:text-gray-900'
                  }`}
                >
                  {customer === 'ALL' ? 'ลูกค้าทั้งหมด' : customer}
                </span>
              </div>
              {customer !== 'ALL' && (
                <span className={`
                  text-xs px-2 py-0.5 rounded-full
                  ${activeCustomer === customer ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}
                `}>
                  {customerCounts[customer] || 0}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerFilter;